import { useState, useEffect, useCallback } from 'react';
import { 
  Smartphone, 
  Monitor, 
  Globe, 
  Bell, 
  Settings,
  Download,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { fetchPWASettings, updatePWASettings, fetchPWAStatus, type PWASettings } from '../services/sync-share-service';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function PWASettings() {
  const [pwaSettings, setPwaSettings] = useState<PWASettings>({
    install_prompt: true,
    auto_update: true,
    notification_badge: true,
    background_sync: true,
    offline_pages: ['dashboard', 'settings'],
    theme_color: '#6366f1',
    display_mode: 'standalone',
    orientation: 'any',
  });
  const [installabilityStatus, setInstallabilityStatus] = useState('ready');
  const [latestVersion, setLatestVersion] = useState('2.4.1');
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installMessage, setInstallMessage] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      const [settings, status] = await Promise.all([
        fetchPWASettings(),
        fetchPWAStatus(),
      ]);
      setPwaSettings(settings);
      setLatestVersion(status.version ?? '2.4.1');
      setUpdateAvailable(status.update_available ?? false);
      setInstallabilityStatus(status.installable ? 'ready' : 'needs-attention');
    } catch (err) {
      console.error('Failed to load PWA settings:', err);
    }
  }, []);

  useEffect(() => {
    loadData();
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [loadData]);

  const persistSettings = async (updates: Partial<PWASettings>) => {
    const next = { ...pwaSettings, ...updates };
    setPwaSettings(next);
    try {
      const saved = await updatePWASettings(updates);
      setPwaSettings(saved);
    } catch (err) {
      console.error('Failed to save PWA settings:', err);
    }
  };

  const toggleSetting = (key: keyof PWASettings) => {
    const value = pwaSettings[key];
    if (typeof value === 'boolean') {
      persistSettings({ [key]: !value } as Partial<PWASettings>);
    }
  };

  const triggerInstall = async () => {
    if (installPrompt) {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      setInstallMessage(choice.outcome === 'accepted' ? 'App installed' : 'Install dismissed');
      setInstallPrompt(null);
      return;
    }
    setInstallMessage('Install prompt not available in this browser. Use Add to Home Screen from the browser menu.');
  };

  const checkForUpdates = async () => {
    try {
      const status = await fetchPWAStatus();
      setUpdateAvailable(status.update_available ?? false);
      setLatestVersion(status.version ?? latestVersion);
      setInstallMessage(status.update_available ? 'Update available' : 'You are up to date');
    } catch (err) {
      console.error('Failed to check updates:', err);
    }
  };

  return (
    <div className="p-6 space-y-6 bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-khiimori-text-primary">PWA Settings</h1>
          <p className="text-sm text-khiimori-text-muted mt-1">
            Configure Progressive Web App behavior and installation options
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            installabilityStatus === 'ready' 
              ? 'bg-success-500/10 text-success-400'
              : 'bg-warning-500/10 text-warning-400'
          }`}>
            {installabilityStatus === 'ready' ? 'Installable' : 'Needs Attention'}
          </span>
        </div>
      </div>

      {installMessage && (
        <p className="text-sm text-khiimori-text-secondary">{installMessage}</p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary-500/10 rounded-lg">
              <Download className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <h2 className="font-semibold text-khiimori-text-primary">Installation</h2>
              <p className="text-sm text-khiimori-text-muted">App installation behavior</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-khiimori-bg-elevated/5 rounded-lg">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-khiimori-text-muted" />
                <div>
                  <p className="font-medium text-khiimori-text-primary">Install Prompt</p>
                  <p className="text-xs text-khiimori-text-muted">Show install prompt to new users</p>
                </div>
              </div>
              <button
                onClick={() => toggleSetting('install_prompt')}
                className={`p-2 rounded-full transition-colors ${
                  pwaSettings.install_prompt
                    ? 'bg-primary-500 text-white'
                    : 'bg-khiimori-bg-elevated/5 text-khiimori-text-muted'
                }`}
              >
                {pwaSettings.install_prompt ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
              </button>
            </div>

            <div className="flex items-center justify-between p-4 bg-khiimori-bg-elevated/5 rounded-lg">
              <div className="flex items-center gap-3">
                <RefreshCw className="w-5 h-5 text-khiimori-text-muted" />
                <div>
                  <p className="font-medium text-khiimori-text-primary">Auto Update</p>
                  <p className="text-xs text-khiimori-text-muted">Automatically update when new version available</p>
                </div>
              </div>
              <button
                onClick={() => toggleSetting('auto_update')}
                className={`p-2 rounded-full transition-colors ${
                  pwaSettings.auto_update
                    ? 'bg-primary-500 text-white'
                    : 'bg-khiimori-bg-elevated/5 text-khiimori-text-muted'
                }`}
              >
                {pwaSettings.auto_update ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
              </button>
            </div>

            <Button onClick={triggerInstall} variant="primary" className="w-full">
              <Download className="w-4 h-4" />
              Install App Now
            </Button>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary-500/10 rounded-lg">
              <Globe className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <h2 className="font-semibold text-khiimori-text-primary">Appearance</h2>
              <p className="text-sm text-khiimori-text-muted">App appearance and theme</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">Theme Color</label>
              <div className="flex items-center gap-3">
                <Input
                  type="color"
                  value={pwaSettings.theme_color}
                  onChange={(e) => persistSettings({ theme_color: e.target.value })}
                  className="w-12 h-12 rounded-lg border-2 border-khiimori-border/10 cursor-pointer"
                />
                <Input
                  type="text"
                  value={pwaSettings.theme_color}
                  onChange={(e) => persistSettings({ theme_color: e.target.value })}
                  placeholder="#6366f1"
                  className="flex-1"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">Display Mode</label>
              <div className="grid grid-cols-3 gap-2">
                {['standalone', 'minimal-ui', 'browser'].map(mode => (
                  <button
                    key={mode}
                    onClick={() => persistSettings({ display_mode: mode })}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      pwaSettings.display_mode === mode
                        ? 'bg-primary-500 text-white'
                        : 'bg-khiimori-bg-elevated/5 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/5'
                    }`}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">Orientation</label>
              <div className="grid grid-cols-2 gap-2">
                {['any', 'portrait', 'landscape'].map(orientation => (
                  <button
                    key={orientation}
                    onClick={() => persistSettings({ orientation })}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      pwaSettings.orientation === orientation
                        ? 'bg-primary-500 text-white'
                        : 'bg-khiimori-bg-elevated/5 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/5'
                    }`}
                  >
                    {orientation.charAt(0).toUpperCase() + orientation.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-success-500/10 rounded-lg">
              <RefreshCw className="w-5 h-5 text-success-400" />
            </div>
            <div>
              <h2 className="font-semibold text-khiimori-text-primary">Sync Settings</h2>
              <p className="text-sm text-khiimori-text-muted">Data synchronization behavior</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-khiimori-bg-elevated/5 rounded-lg">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-khiimori-text-muted" />
                <div>
                  <p className="font-medium text-khiimori-text-primary">Background Sync</p>
                  <p className="text-xs text-khiimori-text-muted">Sync data even when app is closed</p>
                </div>
              </div>
              <button
                onClick={() => toggleSetting('background_sync')}
                className={`p-2 rounded-full transition-colors ${
                  pwaSettings.background_sync
                    ? 'bg-primary-500 text-white'
                    : 'bg-khiimori-bg-elevated/5 text-khiimori-text-muted'
                }`}
              >
                {pwaSettings.background_sync ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
              </button>
            </div>

            <div className="flex items-center justify-between p-4 bg-khiimori-bg-elevated/5 rounded-lg">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-khiimori-text-muted" />
                <div>
                  <p className="font-medium text-khiimori-text-primary">Notification Badge</p>
                  <p className="text-xs text-khiimori-text-muted">Show unread count on app icon</p>
                </div>
              </div>
              <button
                onClick={() => toggleSetting('notification_badge')}
                className={`p-2 rounded-full transition-colors ${
                  pwaSettings.notification_badge
                    ? 'bg-primary-500 text-white'
                    : 'bg-khiimori-bg-elevated/5 text-khiimori-text-muted'
                }`}
              >
                {pwaSettings.notification_badge ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">Offline Pages</label>
              <div className="flex flex-wrap gap-2">
                {['dashboard', 'settings', 'profile', 'help', 'sync'].map(page => (
                  <button
                    key={page}
                    onClick={() => {
                      const newOffline = pwaSettings.offline_pages.includes(page)
                        ? pwaSettings.offline_pages.filter(p => p !== page)
                        : [...pwaSettings.offline_pages, page];
                      persistSettings({ offline_pages: newOffline });
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      pwaSettings.offline_pages.includes(page)
                        ? 'bg-primary-500 text-white'
                        : 'bg-khiimori-bg-elevated/5 text-khiimori-text-muted'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-info-500/10 rounded-lg">
              <Settings className="w-5 h-5 text-info-400" />
            </div>
            <div>
              <h2 className="font-semibold text-khiimori-text-primary">App Status</h2>
              <p className="text-sm text-khiimori-text-muted">Current version and updates</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="p-4 bg-khiimori-bg-elevated/5 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-khiimori-text-muted">Current Version</span>
                <span className="text-lg font-bold text-khiimori-text-primary">{latestVersion}</span>
              </div>
              {updateAvailable ? (
                <div className="flex items-center gap-2 text-success-400">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">Update available</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-success-400">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">You're up to date</span>
                </div>
              )}
            </div>

            <Button onClick={checkForUpdates} variant="secondary" className="w-full">
              <RefreshCw className="w-4 h-4" />
              Check for Updates
            </Button>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-semibold text-khiimori-text-primary">Device Compatibility</h2>
          <div className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-khiimori-text-muted" />
            <Monitor className="w-5 h-5 text-khiimori-text-muted" />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-success-500/10 rounded-lg border border-success-500/20">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-success-400" />
              <span className="font-medium text-khiimori-text-primary">iOS Safari</span>
            </div>
            <p className="text-xs text-khiimori-text-muted">Full support with add to homescreen</p>
          </div>
          
          <div className="p-4 bg-success-500/10 rounded-lg border border-success-500/20">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-success-400" />
              <span className="font-medium text-khiimori-text-primary">Android Chrome</span>
            </div>
            <p className="text-xs text-khiimori-text-muted">Full support with install prompt</p>
          </div>
          
          <div className="p-4 bg-warning-500/10 rounded-lg border border-warning-500/20">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-warning-400" />
              <span className="font-medium text-khiimori-text-primary">Desktop Browsers</span>
            </div>
            <p className="text-xs text-khiimori-text-muted">Limited support, may require manual install</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
